import { RepairRequest, RepairStatus } from '../types/firestore';
import { getRepairRequestId } from './repairRequestReadModel';
import { isTerminalRepairRequestStatus } from './repairRequestWorkflow';

export interface ServicePlannerItem {
  request: RepairRequest;
  requestId: string;
  scheduledStartAt: Date | null;
  scheduledEndAt: Date | null;
  clientPreferenceDate: Date | null;
  hasScheduleConflict: boolean;
  scheduleConflictRequestIds: string[];
}

export type ServicePlannerDayLoadLevel = 'empty' | 'light' | 'normal' | 'heavy';

export interface ServicePlannerDayWorkloadSummary {
  dayKey: string;
  scheduledCount: number;
  firstScheduledAt: Date | null;
  lastScheduledAt: Date | null;
  conflictCount: number;
  loadLevel: ServicePlannerDayLoadLevel;
}

export interface ServicePlannerWeekDay {
  dayKey: string;
  date: Date;
  scheduledCount: number;
  clientPreferenceCount: number;
  conflictCount: number;
  loadLevel: ServicePlannerDayLoadLevel;
  workloadSummary: ServicePlannerDayWorkloadSummary;
  items: ServicePlannerItem[];
  isToday: boolean;
  isSelected: boolean;
}

export interface ServicePlannerTimeGridBlock {
  item: ServicePlannerItem;
  startMinutes: number;
  endMinutes: number | null;
  visualEndMinutes: number;
  durationMinutes: number | null;
  visualDurationMinutes: number;
  topOffsetMinutes: number;
  hasConflict: boolean;
}

export interface ServicePlannerDayTimeGrid {
  dayKey: string;
  rangeStartHour: number;
  rangeEndHour: number;
  hours: number[];
  blocks: ServicePlannerTimeGridBlock[];
}

export interface ServicePlannerReadModel {
  scheduledItems: ServicePlannerItem[];
  unscheduledItems: ServicePlannerItem[];
  clientPreferenceItems: ServicePlannerItem[];
  todayItems: ServicePlannerItem[];
  upcomingItems: ServicePlannerItem[];
  selectedDayKey: string;
  selectedDayItems: ServicePlannerItem[];
  selectedDayWorkloadSummary: ServicePlannerDayWorkloadSummary;
  selectedDayTimeGrid: ServicePlannerDayTimeGrid;
  weekStartDayKey: string;
  weekItems: ServicePlannerWeekDay[];
  scheduledItemsByDay: Record<string, ServicePlannerItem[]>;
  scheduledCountsByDay: Record<string, number>;
  clientPreferenceCountsByDay: Record<string, number>;
  workloadSummaryByDay: Record<string, ServicePlannerDayWorkloadSummary>;
}

export const SERVICE_PLANNER_TIME_ZONE = 'Europe/Warsaw';
export const SERVICE_PLANNER_DEFAULT_DAY_START_HOUR = 8;
export const SERVICE_PLANNER_DEFAULT_DAY_END_HOUR = 18;
export const SERVICE_PLANNER_FALLBACK_DURATION_MINUTES = 60;
export const SERVICE_PLANNER_MIN_BLOCK_DURATION_MINUTES = 45;
const KNOWN_REPAIR_STATUSES = new Set(Object.values(RepairStatus));

const warsawDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: SERVICE_PLANNER_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const warsawTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: SERVICE_PLANNER_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const toDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value === 'object') {
    const timestampLike = value as {
      toDate?: () => Date;
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };

    if (typeof timestampLike.toDate === 'function') {
      const date = timestampLike.toDate();
      if (date instanceof Date && Number.isFinite(date.getTime())) {
        return date;
      }
    }

    if (typeof timestampLike.toMillis === 'function') {
      const date = new Date(timestampLike.toMillis());
      if (Number.isFinite(date.getTime())) {
        return date;
      }
    }

    const seconds = timestampLike.seconds ?? timestampLike._seconds;
    const nanoseconds = timestampLike.nanoseconds ?? timestampLike._nanoseconds ?? 0;
    if (
      Number.isInteger(seconds) &&
      Number.isInteger(nanoseconds) &&
      (nanoseconds as number) >= 0 &&
      (nanoseconds as number) <= 999999999
    ) {
      const date = new Date((seconds as number) * 1000 + Math.floor((nanoseconds as number) / 1e6));
      return Number.isFinite(date.getTime()) ? date : null;
    }
  }

  return null;
};

export const getServicePlannerDayKey = (date: Date): string => warsawDateFormatter.format(date);

export const getServicePlannerMinutesOfDay = (date: Date): number | null => {
  if (!Number.isFinite(date.getTime())) {
    return null;
  }

  const parts = warsawTimeFormatter.formatToParts(date);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value);

  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  return hour * 60 + minute;
};

export const dateFromServicePlannerDayKey = (dayKey: string): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!match) {
    return new Date(NaN);
  }

  const [, yearRaw, monthRaw, dayRaw] = match;
  return new Date(Date.UTC(Number(yearRaw), Number(monthRaw) - 1, Number(dayRaw), 12, 0, 0, 0));
};

export const addDaysToServicePlannerDayKey = (dayKey: string, days: number): string => {
  const date = dateFromServicePlannerDayKey(dayKey);
  if (!Number.isFinite(date.getTime())) {
    return dayKey;
  }

  date.setUTCDate(date.getUTCDate() + days);
  return getServicePlannerDayKey(date);
};

export const getServicePlannerWeekDayKeys = (dayKey: string): string[] => {
  const date = dateFromServicePlannerDayKey(dayKey);
  if (!Number.isFinite(date.getTime())) {
    return [];
  }

  const mondayOffset = (date.getUTCDay() + 6) % 7;
  const mondayKey = addDaysToServicePlannerDayKey(dayKey, -mondayOffset);
  return Array.from({ length: 7 }, (_, index) => addDaysToServicePlannerDayKey(mondayKey, index));
};

const isTerminalRequest = (request: RepairRequest): boolean => {
  const status = request.status;
  if (!KNOWN_REPAIR_STATUSES.has(status)) {
    return false;
  }
  return isTerminalRepairRequestStatus(status);
};

const sortByDateAsc = (
  items: ServicePlannerItem[],
  selectDate: (item: ServicePlannerItem) => Date | null
): ServicePlannerItem[] =>
  [...items].sort((a, b) => {
    const aMs = selectDate(a)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bMs = selectDate(b)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return aMs - bMs;
  });

const sortByRequestFreshnessDesc = (items: ServicePlannerItem[]): ServicePlannerItem[] =>
  [...items].sort((a, b) => {
    const aMs =
      toDate(a.request.updatedAt)?.getTime() ?? toDate(a.request.createdAt)?.getTime() ?? 0;
    const bMs =
      toDate(b.request.updatedAt)?.getTime() ?? toDate(b.request.createdAt)?.getTime() ?? 0;
    return bMs - aMs;
  });

export const groupServicePlannerScheduledItemsByDay = (
  items: ServicePlannerItem[]
): Record<string, ServicePlannerItem[]> =>
  items.reduce<Record<string, ServicePlannerItem[]>>((groups, item) => {
    if (!item.scheduledStartAt) {
      return groups;
    }

    const dayKey = getServicePlannerDayKey(item.scheduledStartAt);
    groups[dayKey] = groups[dayKey] || [];
    groups[dayKey].push(item);
    return groups;
  }, {});

export const getServicePlannerDayLoadLevel = (
  scheduledCount: number,
  conflictCount: number
): ServicePlannerDayLoadLevel => {
  if (scheduledCount <= 0) {
    return 'empty';
  }
  if (scheduledCount >= 5 || conflictCount >= 2) {
    return 'heavy';
  }
  if (scheduledCount === 1) {
    return 'light';
  }
  return 'normal';
};

export const deriveServicePlannerDayWorkloadSummary = (
  dayKey: string,
  items: ServicePlannerItem[]
): ServicePlannerDayWorkloadSummary => {
  const scheduledDates = items
    .map((item) => item.scheduledStartAt)
    .filter((date): date is Date => date instanceof Date && Number.isFinite(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());
  const scheduledCount = scheduledDates.length;
  const conflictCount = items.filter(
    (item) => item.scheduledStartAt && item.hasScheduleConflict
  ).length;

  return {
    dayKey,
    scheduledCount,
    firstScheduledAt: scheduledDates[0] || null,
    lastScheduledAt: scheduledDates[scheduledDates.length - 1] || null,
    conflictCount,
    loadLevel: getServicePlannerDayLoadLevel(scheduledCount, conflictCount),
  };
};

export const detectServicePlannerScheduleConflicts = (
  items: ServicePlannerItem[]
): Record<string, string[]> => {
  const conflicts: Record<string, Set<string>> = {};

  const addConflict = (a: ServicePlannerItem, b: ServicePlannerItem) => {
    conflicts[a.requestId] = conflicts[a.requestId] || new Set<string>();
    conflicts[b.requestId] = conflicts[b.requestId] || new Set<string>();
    conflicts[a.requestId].add(b.requestId);
    conflicts[b.requestId].add(a.requestId);
  };

  for (let firstIndex = 0; firstIndex < items.length; firstIndex += 1) {
    const first = items[firstIndex];
    if (!first.scheduledStartAt) {
      continue;
    }

    for (let secondIndex = firstIndex + 1; secondIndex < items.length; secondIndex += 1) {
      const second = items[secondIndex];
      if (!second.scheduledStartAt) {
        continue;
      }

      const sameStart = first.scheduledStartAt.getTime() === second.scheduledStartAt.getTime();
      const firstEnd = first.scheduledEndAt;
      const secondEnd = second.scheduledEndAt;
      const hasRangeOverlap =
        Boolean(firstEnd && secondEnd) &&
        first.scheduledStartAt.getTime() < (secondEnd as Date).getTime() &&
        second.scheduledStartAt.getTime() < (firstEnd as Date).getTime();

      if (sameStart || hasRangeOverlap) {
        addConflict(first, second);
      }
    }
  }

  return Object.fromEntries(
    Object.entries(conflicts).map(([requestId, requestIds]) => [
      requestId,
      Array.from(requestIds).sort(),
    ])
  );
};

export const deriveServicePlannerDayTimeGrid = (
  dayKey: string,
  items: ServicePlannerItem[]
): ServicePlannerDayTimeGrid => {
  const blocks = items
    .map<ServicePlannerTimeGridBlock | null>((item) => {
      if (!item.scheduledStartAt) {
        return null;
      }

      const startMinutes = getServicePlannerMinutesOfDay(item.scheduledStartAt);
      if (startMinutes === null) {
        return null;
      }

      const endMinutes =
        item.scheduledEndAt && getServicePlannerDayKey(item.scheduledEndAt) === dayKey
          ? getServicePlannerMinutesOfDay(item.scheduledEndAt)
          : null;
      const validEndMinutes = endMinutes !== null && endMinutes > startMinutes ? endMinutes : null;
      const durationMinutes = validEndMinutes !== null ? validEndMinutes - startMinutes : null;
      const visualDurationMinutes = Math.max(
        durationMinutes ?? SERVICE_PLANNER_FALLBACK_DURATION_MINUTES,
        SERVICE_PLANNER_MIN_BLOCK_DURATION_MINUTES
      );
      const visualEndMinutes = Math.min(startMinutes + visualDurationMinutes, 24 * 60);

      return {
        item,
        startMinutes,
        endMinutes: validEndMinutes,
        visualEndMinutes,
        durationMinutes,
        visualDurationMinutes: visualEndMinutes - startMinutes,
        topOffsetMinutes: 0,
        hasConflict: item.hasScheduleConflict,
      };
    })
    .filter((block): block is ServicePlannerTimeGridBlock => Boolean(block));

  const earliestStartMinutes =
    blocks.length > 0
      ? Math.min(...blocks.map((block) => block.startMinutes))
      : SERVICE_PLANNER_DEFAULT_DAY_START_HOUR * 60;
  const latestVisualEndMinutes =
    blocks.length > 0
      ? Math.max(...blocks.map((block) => block.visualEndMinutes))
      : SERVICE_PLANNER_DEFAULT_DAY_END_HOUR * 60;
  const rangeStartHour = Math.min(
    SERVICE_PLANNER_DEFAULT_DAY_START_HOUR,
    Math.floor(earliestStartMinutes / 60)
  );
  const latestVisualEndHour = Math.ceil(latestVisualEndMinutes / 60);
  const rangeEndHour =
    latestVisualEndHour > SERVICE_PLANNER_DEFAULT_DAY_END_HOUR
      ? Math.min(24, latestVisualEndHour + 1)
      : SERVICE_PLANNER_DEFAULT_DAY_END_HOUR;
  const rangeStartMinutes = rangeStartHour * 60;

  return {
    dayKey,
    rangeStartHour,
    rangeEndHour,
    hours: Array.from(
      { length: Math.max(0, rangeEndHour - rangeStartHour) },
      (_, index) => rangeStartHour + index
    ),
    blocks: blocks.map((block) => ({
      ...block,
      topOffsetMinutes: Math.max(0, block.startMinutes - rangeStartMinutes),
    })),
  };
};

export const deriveServicePlannerItems = (
  requests: RepairRequest[],
  options: { referenceDate?: Date; selectedDayKey?: string; selectedDate?: Date } = {}
): ServicePlannerReadModel => {
  const scheduledItemsRaw: ServicePlannerItem[] = [];
  const unscheduledItemsRaw: ServicePlannerItem[] = [];
  const clientPreferenceItemsRaw: ServicePlannerItem[] = [];
  const scheduledCountsByDay: Record<string, number> = {};
  const clientPreferenceCountsByDay: Record<string, number> = {};

  requests.forEach((request) => {
    const requestId = getRepairRequestId(request);
    if (!requestId || isTerminalRequest(request)) {
      return;
    }

    const serviceSchedule = request.serviceSchedule;
    const scheduledStartAt =
      serviceSchedule?.status === 'scheduled' ? toDate(serviceSchedule.scheduledStartAt) : null;
    const scheduledEndAt =
      serviceSchedule?.status === 'scheduled' ? toDate(serviceSchedule.scheduledEndAt) : null;
    const clientPreferenceDate = toDate(request.scheduledDate);
    const item: ServicePlannerItem = {
      request,
      requestId,
      scheduledStartAt,
      scheduledEndAt,
      clientPreferenceDate,
      hasScheduleConflict: false,
      scheduleConflictRequestIds: [],
    };

    if (clientPreferenceDate) {
      clientPreferenceItemsRaw.push(item);
      const preferenceDayKey = getServicePlannerDayKey(clientPreferenceDate);
      clientPreferenceCountsByDay[preferenceDayKey] =
        (clientPreferenceCountsByDay[preferenceDayKey] || 0) + 1;
    }

    if (serviceSchedule?.status === 'scheduled' && scheduledStartAt) {
      scheduledItemsRaw.push(item);
      const scheduledDayKey = getServicePlannerDayKey(scheduledStartAt);
      scheduledCountsByDay[scheduledDayKey] = (scheduledCountsByDay[scheduledDayKey] || 0) + 1;
      return;
    }

    unscheduledItemsRaw.push(item);
  });

  const conflictRequestIdsByItemId = detectServicePlannerScheduleConflicts(scheduledItemsRaw);
  const annotateItem = (item: ServicePlannerItem): ServicePlannerItem => {
    const scheduleConflictRequestIds = conflictRequestIdsByItemId[item.requestId] || [];
    return {
      ...item,
      hasScheduleConflict: scheduleConflictRequestIds.length > 0,
      scheduleConflictRequestIds,
    };
  };
  const annotatedItemsById = new Map<string, ServicePlannerItem>();
  [...scheduledItemsRaw, ...unscheduledItemsRaw].forEach((item) => {
    if (!annotatedItemsById.has(item.requestId)) {
      annotatedItemsById.set(item.requestId, annotateItem(item));
    }
  });
  const readAnnotatedItem = (item: ServicePlannerItem): ServicePlannerItem =>
    annotatedItemsById.get(item.requestId) || annotateItem(item);
  const sortedUnscheduledItems = sortByRequestFreshnessDesc(
    unscheduledItemsRaw.map(readAnnotatedItem)
  );
  const sortedClientPreferenceItems = sortByDateAsc(
    clientPreferenceItemsRaw.map(readAnnotatedItem),
    (item) => item.clientPreferenceDate
  );
  const sortedScheduledItems = sortByDateAsc(
    scheduledItemsRaw.map(readAnnotatedItem),
    (item) => item.scheduledStartAt
  );
  const scheduledItemsByDay = groupServicePlannerScheduledItemsByDay(sortedScheduledItems);
  const workloadSummaryByDay = Object.fromEntries(
    Object.entries(scheduledItemsByDay).map(([dayKey, dayItems]) => [
      dayKey,
      deriveServicePlannerDayWorkloadSummary(dayKey, dayItems),
    ])
  );
  const getWorkloadSummaryForDay = (dayKey: string): ServicePlannerDayWorkloadSummary =>
    workloadSummaryByDay[dayKey] || deriveServicePlannerDayWorkloadSummary(dayKey, []);
  const referenceDate = options.referenceDate || new Date();
  const todayKey = getServicePlannerDayKey(referenceDate);
  const selectedDayKey =
    options.selectedDayKey ||
    (options.selectedDate ? getServicePlannerDayKey(options.selectedDate) : todayKey);
  const weekDayKeys = getServicePlannerWeekDayKeys(selectedDayKey);
  const weekItems = weekDayKeys.map<ServicePlannerWeekDay>((dayKey) => {
    const dayItems = scheduledItemsByDay[dayKey] || [];
    const workloadSummary = getWorkloadSummaryForDay(dayKey);
    return {
      dayKey,
      date: dateFromServicePlannerDayKey(dayKey),
      scheduledCount: scheduledCountsByDay[dayKey] || 0,
      clientPreferenceCount: clientPreferenceCountsByDay[dayKey] || 0,
      conflictCount: workloadSummary.conflictCount,
      loadLevel: workloadSummary.loadLevel,
      workloadSummary,
      items: dayItems,
      isToday: dayKey === todayKey,
      isSelected: dayKey === selectedDayKey,
    };
  });
  const selectedDayItems = scheduledItemsByDay[selectedDayKey] || [];
  const selectedDayWorkloadSummary = getWorkloadSummaryForDay(selectedDayKey);

  return {
    scheduledItems: sortedScheduledItems,
    unscheduledItems: sortedUnscheduledItems,
    clientPreferenceItems: sortedClientPreferenceItems,
    todayItems: sortedScheduledItems.filter(
      (item) => item.scheduledStartAt && getServicePlannerDayKey(item.scheduledStartAt) === todayKey
    ),
    upcomingItems: sortedScheduledItems.filter(
      (item) => item.scheduledStartAt && getServicePlannerDayKey(item.scheduledStartAt) > todayKey
    ),
    selectedDayKey,
    selectedDayItems,
    selectedDayWorkloadSummary,
    selectedDayTimeGrid: deriveServicePlannerDayTimeGrid(selectedDayKey, selectedDayItems),
    weekStartDayKey: weekDayKeys[0] || selectedDayKey,
    weekItems,
    scheduledItemsByDay,
    scheduledCountsByDay,
    clientPreferenceCountsByDay,
    workloadSummaryByDay,
  };
};
